import { EMAIL_RE } from 'utils/validation/email'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'

import { ONSITE_SHOOTING_CATEGORY_IDS } from '../catalogueConstants'
import type { PfStoreState } from '../store'

/**
 * Whether the creative team arranges the shoot unaided: `false` when any line is in an on-site
 * shooting category (`ONSITE_SHOOTING_CATEGORY_IDS`), so the client must name a meeting. An empty cart
 * or a catalogue not yet loaded answers `true`, so a meeting is never demanded before it is known to
 * be needed.
 */
export const selectDefaultScheduledByCT =
  (catalogue: { shellProducts: Array<{ id: string; categoryId: string }> } | undefined) =>
    (state: PfStoreState): boolean => {
      if (state.items.length === 0) return true
      if (!catalogue) return true

      for (const item of state.items) {
        const shell = catalogue.shellProducts.find((s) => s.id === item.shellProductId)
        if (shell && ONSITE_SHOOTING_CATEGORY_IDS.has(shell.categoryId)) {
          return false
        }
      }
      return true
    }

/**
 * Whether the Instructions step is skipped. The backend's `visible` verdict from the instruction
 * options decides when it has arrived; it is the same site-access rule placement validates against.
 *
 * Until then the cart shape stands in, so the stepper does not show and hide the step: skipped when
 * there are lines, no extras chosen, and none of the lines is in an on-site shooting category.
 */
export const selectInstructionsStepSkipped =
  (catalogue?: { shellProducts: Array<{ id: string; categoryId: string }> }, instructionsVisible?: boolean) =>
    (state: PfStoreState): boolean => {
      if (instructionsVisible !== undefined) return !instructionsVisible

      if (state.items.length === 0) return false
      if (state.extraServices.length > 0) return false

      if (!catalogue) return false

      for (const item of state.items) {
        const shell = catalogue.shellProducts.find((s) => s.id === item.shellProductId)
        if (shell && ONSITE_SHOOTING_CATEGORY_IDS.has(shell.categoryId)) {
          return false
        }
      }

      return true
    }

/**
 * The Instructions step's gate. A step the backend hides asks for nothing; otherwise a cart that needs
 * site access must name a meeting, and a named meeting must be valid for its mode — contact name and a
 * phone the phone input accepts, well-formed emails, a date within the bookable window, and for
 * a key pickup a geocoded address with its travel cost quoted.
 */
export const selectInstructionsStepValid =
  (catalogue?: { shellProducts: Array<{ id: string; categoryId: string }> }, instructionsVisible?: boolean) =>
    (state: PfStoreState): boolean => {
      if (instructionsVisible === false) return true

      if (state.items.length === 0) return false

      const { meeting, ctBookingId, meetingPhoneInvalid, meetingDateTooSoon, meetingDateTooFar, keyPickupTravelQuotePending } = state

      if (ctBookingId !== undefined && ctBookingId.length === 0) return false

      const meetingRequired = !selectDefaultScheduledByCT(catalogue)(state)

      // `NONE` is no longer offered; one surviving in an old draft counts as nothing chosen.
      const chosenMeeting = meeting && meeting.mode !== 'NONE' ? meeting : undefined

      if (meetingRequired && !chosenMeeting) return false

      if (chosenMeeting) {
        if (meetingPhoneInvalid) return false
        // Derived by the page, which holds the catalogue timezone the lead-time floor depends on.
        if (meetingDateTooSoon) return false
        if (meetingDateTooFar) return false

        switch (chosenMeeting.mode) {
          case 'ON_SITE': {
            const m = chosenMeeting.onSite
            if (!m?.date || !m.contactName || !m.contactPhone) return false
            if (m.contactEmail && !EMAIL_RE.test(m.contactEmail)) return false
            break
          }
          case 'KEY_PICKUP': {
            const m = chosenMeeting.keyPickup
            if (!m) return false
            if (!m.date && !m.arrangedByCreative) return false
            if (!m.contactName || !m.contactPhone || !m.address) return false
            if (m.contactEmail && !EMAIL_RE.test(m.contactEmail)) return false
            // Only a geocoded pick has the routable coordinates the wire requires.
            if (!m.coordinates) return false
            // The order bills the travel cost by distance; it must be quoted before the client goes on.
            if (keyPickupTravelQuotePending) return false
            break
          }
          case 'THIRD_PARTY': {
            const m = chosenMeeting.thirdParty
            if (!m?.name) return false
            // Phone required, email optional, as the field markers promise.
            if (!m.phone) return false
            if (m.email && !EMAIL_RE.test(m.email)) return false
            break
          }
          case 'NONE':
            break
        }
      }

      return true
    }

/**
 * Why the Instructions gate is closed, as `validation_errors` codes; empty when it is open. Mirrors
 * `selectInstructionsStepValid` rule for rule, so change the two together; unlike the gate it names
 * every gap of the chosen meeting, as legacy did.
 */
export const selectInstructionsStepErrors =
  (catalogue?: { shellProducts: Array<{ id: string; categoryId: string }> }, instructionsVisible?: boolean) =>
    (state: PfStoreState): NextStepValidationError[] => {
      if (instructionsVisible === false) return []
      if (state.items.length === 0) return ['product_missing']

      const { meeting, ctBookingId, meetingPhoneInvalid, meetingDateTooSoon, meetingDateTooFar, keyPickupTravelQuotePending } = state
      const errors = new Set<NextStepValidationError>()

      if (ctBookingId !== undefined && ctBookingId.length === 0) errors.add('favourite_creative_missing')

      const chosenMeeting = meeting && meeting.mode !== 'NONE' ? meeting : undefined
      if (!chosenMeeting) {
        if (!selectDefaultScheduledByCT(catalogue)(state)) errors.add('meeting_type_missing')
        return Array.from(errors)
      }

      if (meetingPhoneInvalid) errors.add('meeting_phone_invalid')
      if (meetingDateTooSoon || meetingDateTooFar) errors.add('meeting_date_invalid')

      const contact = (name?: string, phone?: string, email?: string) => {
        if (!name) errors.add('meeting_name_invalid')
        if (!phone) errors.add('meeting_phone_invalid')
        if (email && !EMAIL_RE.test(email)) errors.add('meeting_email_invalid')
      }
      switch (chosenMeeting.mode) {
        case 'ON_SITE': {
          const m = chosenMeeting.onSite
          if (!m?.date) errors.add('meeting_date_invalid')
          contact(m?.contactName, m?.contactPhone, m?.contactEmail)
          break
        }
        case 'KEY_PICKUP': {
          const m = chosenMeeting.keyPickup
          if (!m?.date && !m?.arrangedByCreative) errors.add('meeting_date_invalid')
          contact(m?.contactName, m?.contactPhone, m?.contactEmail)
          if (!m?.address || !m.coordinates) errors.add('meeting_address_invalid')
          if (keyPickupTravelQuotePending) errors.add('prices_recalculating')
          break
        }
        case 'THIRD_PARTY': {
          const m = chosenMeeting.thirdParty
          contact(m?.name, m?.phone, m?.email)
          break
        }
      }

      return Array.from(errors)
    }

/**
 * The wire `scheduledByCT`, derived from the meeting mode: a third party means the creative arranges
 * access (true); a meeting on site, a key pickup or no meeting means the client does (false).
 */
export const selectScheduledByCT = (state: PfStoreState): boolean =>
  state.meeting?.mode === 'THIRD_PARTY'
